import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useDecryptCpr } from '@/hooks/usePatients';
import { formatCprForPrint, printHtml } from '@/lib/referral/referralPrint';
import { ggopPrintHtml, ggopScreenHtml, renderGgop, type GgopRendered } from '@/lib/ggop/ggopView';
import { loadXdisStylesheet, withXdisCpr, xdisHeading } from '@/lib/xdis/xdisView';

// Spor A block 3 (XDIS-EPJ 3.3, 3.7.1, 7.x): one XDIS16 or XDIS18 shown with MedCom's
// stylesheet, printable with heading, patient name and CPR on every page. The XML arrives
// with every CPR masked; the patient's CPR is read via decrypt-cpr (audited) only when
// the report is coupled to a patient. A relative's CPR is never shown.

export function XdisViewer({
  xml,
  kind,
  patientId,
  patientName,
  canRevealCpr = true,
}: {
  xml: string;
  kind: string;
  patientId: string | null;
  patientName: string | null;
  canRevealCpr?: boolean;
}) {
  const { t } = useTranslation();
  const decrypt = useDecryptCpr();
  const [xsl, setXsl] = useState<string | null>(null);
  const [cpr, setCpr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const heading = xdisHeading(kind);
  const hidden = t('patients.xdis.cpr_hidden', 'CPR skjult');

  useEffect(() => {
    let cancelled = false;
    loadXdisStylesheet().then(
      (value) => !cancelled && setXsl(value),
      (err: unknown) => !cancelled && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setCpr(null);
    if (!patientId || !canRevealCpr) return;
    let cancelled = false;
    decrypt.mutateAsync({ patient_id: patientId, source: 'xdis-view', purpose: 'xdis' }).then(
      (result) => !cancelled && setCpr(result.cpr),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // mutateAsync is stable; the mutation object is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, canRevealCpr]);

  const { rendered, renderError } = useMemo<{ rendered: GgopRendered | null; renderError: string | null }>(() => {
    if (!xsl) return { rendered: null, renderError: null };
    try {
      return { rendered: renderGgop(withXdisCpr(xml, cpr, hidden, hidden), xsl), renderError: null };
    } catch (err) {
      return { rendered: null, renderError: err instanceof Error ? err.message : String(err) };
    }
  }, [xml, xsl, cpr, hidden]);
  const shownError = error ?? renderError;

  function onPrint() {
    if (!rendered) return;
    printHtml(
      ggopPrintHtml({
        rendered,
        heading,
        patientName: patientName ?? '',
        cprText: cpr ? formatCprForPrint(cpr) : hidden,
        labels: {
          cpr: 'CPR',
          page: t('patients.xdis.print_page', 'Side'),
          of: t('patients.xdis.print_of', 'af'),
          printedAt: `${t('patients.xdis.printed_at', 'Udskrevet')} ${new Date().toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'short' })}`,
        },
      }),
    );
  }

  return (
    <div className="space-y-2" data-testid="xdis-viewer">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-base font-semibold">{heading}</h4>
        <Button type="button" size="sm" variant="secondary" onClick={onPrint} disabled={!rendered}>
          <Printer className="mr-1.5 h-4 w-4" />
          {t('patients.xdis.print', 'Udskriv')}
        </Button>
      </div>
      {shownError && <p role="alert" className="text-sm text-ds-red-ink">{shownError}</p>}
      {!rendered && !shownError && <p className="text-sm text-ds-muted">{t('patients.xdis.rendering', 'Viser rapporten…')}</p>}
      {rendered && (
        <iframe
          title={heading}
          sandbox=""
          srcDoc={ggopScreenHtml(rendered)}
          className="h-[70vh] w-full rounded-md border border-ds-line bg-ds-card"
        />
      )}
    </div>
  );
}
